"use client";

import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const NodeRewardsDailyBurnChart = ({ data }) => {
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      // Sort by date ascending and take last 30 days
      const sortedData = data
        .sort((a, b) => new Date(a.burn_date) - new Date(b.burn_date))
        .slice(-30)
        .map(item => ({
          date: new Date(item.burn_date).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
          }),
          burned: parseFloat(item.total_burned_amount || 0),
          wallets: item.wallets_affected_count || 0
        }));
      
      setChartData(sortedData);
    }
  }, [data]);

  const formatNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return parseFloat(num).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0});
  };

  if (!data) {
    return (
      <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6">
        <div className="animate-pulse">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-6 bg-[#23272e] rounded w-48"></div>
          </div>
          <div className="h-64 bg-[#23272e] rounded"></div>
        </div>
      </div>
    );
  }

  const totalBurned = chartData.reduce((sum, item) => sum + item.burned, 0);
  const averageBurned = chartData.length > 0 ? totalBurned / chartData.length : 0;
  const maxDailyBurn = Math.max(0, ...chartData.map(item => item.burned));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#0f1419] border border-[#2a2f38] text-white text-xs rounded-lg px-3 py-2 shadow-lg">
          <div className="font-medium">{label}</div>
          <div className="text-red-400">Burned: {formatNumber(payload[0].value)}</div>
          <div className="text-blue-400">Wallets: {payload[0].payload.wallets}</div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">
          Daily Node Rewards To Be Burnt (30 Days)
          </h3>
          <p className="text-sm text-gray-400">
            Token burning trends from node rewards over the past month
          </p>
        </div>
        <Icon icon="ph:chart-bar-bold" className="text-xl text-metaxseed-blue" />
      </div>

      {chartData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 flex-grow">
          <Icon icon="ph:chart-line-duotone" className="text-4xl text-gray-600 mb-4" />
          <p className="text-gray-400">No daily burn data available</p>
        </div>
      ) : (
        <>
          {/* Stats Overview */}
          <div className="grid grid-cols-3 gap-4 my-4">
            <div className="text-center">
              <p className="text-xs text-gray-400">Total Burn Amount</p>
              <p className="text-sm font-semibold text-red-400">{formatNumber(totalBurned)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-400">Daily Average</p>
              <p className="text-sm font-semibold text-blue-400">{formatNumber(averageBurned)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-400">Peak Day</p>
              <p className="text-sm font-semibold text-green-400">{formatNumber(maxDailyBurn)}</p>
            </div>
          </div>

          {/* Chart */}
          <div className="flex-grow w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 5, right: 0, left: -20, bottom: 20 }}
                barGap={2}
              >
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#dc2626" stopOpacity={0.8}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity="0.3" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#9ca3af', fontSize: 12 }} 
                  axisLine={{ stroke: '#374151' }} 
                  tickLine={{ stroke: '#374151' }}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  tickFormatter={formatNumber}
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  axisLine={{ stroke: '#374151' }}
                  tickLine={{ stroke: '#374151' }}
                  width={80}
                />
                <Tooltip 
                  content={<CustomTooltip />} 
                  cursor={{fill: 'rgba(107, 114, 128, 0.1)'}}
                />
                <Bar dataKey="burned" name="Burned Amount" fill="url(#barGradient)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-4 pt-4 border-t border-[#2a2f38]">
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>Data shows burns from node rewards distribution</span>
              <span>{chartData.length} days of data</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NodeRewardsDailyBurnChart;